import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { PROVIDER_PATHS } from "@/constants/paths";
import useDisclosure, { DisclosureHost } from "@/hooks/use-disclosure";
import { useSnackbar } from "@/hooks/useSnackbar";
import { GROUP_SESSION_DETAIL_QUERY_KEY } from "@/queries/group-visit/use-group-session-by-id";
import { GROUP_SESSION_LIST_QUERY_KEY } from "@/queries/group-visit/use-group-sessions";
import { useUpdateGroupSessionStatus } from "@/queries/group-visit/use-update-group-session-status";
import { usePaymentLinkUrl } from "@/queries/use-payment-link-url";
import { UpdateSessionStatusSessionStatusEnum } from "@/services";
import {
  getErrorSnackbar,
  getSuccessSnackbar,
  getXtenantId,
} from "@/utils/utils";
import { GROUP_APPOINTMENT_STATUS } from "@/pages/app/provider/scheduling/group-appointment";
import {
  CHECK_IN_MESSAGE,
  CHECK_IN_ROW_ACTION,
  PAYMENT_LINK_CURRENCY,
} from "../group-visit-constant";
import { CheckInPatient, CheckInRowAction } from "../group-visit-types";
import { withSessionParam } from "../group-visit-utils";

export type UseCheckInActions = {
  onRowAction: (action: CheckInRowAction, patient: CheckInPatient) => void;
  startSession: () => Promise<void>;
  /** Carries the patient the payment drawer collects from. */
  paymentDisclosure: DisclosureHost;
  /** Re-reads the session, so a collected payment shows on the roster. */
  refetchSession: () => void;
};

/**
 * Check-in row actions and Start Session. Sending a link refetches the
 * session so the roster picks up `paymentLinkSent`.
 */
export const useCheckInActions = (
  sessionId?: string,
  sessionStatus?: string
): UseCheckInActions => {
  const client = useQueryClient();
  const navigate = useNavigate();
  const showSnackbar = useSnackbar();
  const paymentDisclosure = useDisclosure();
  const { mutateAsync: sendPaymentLink } = usePaymentLinkUrl();
  const { mutateAsync: updateSessionStatus } = useUpdateGroupSessionStatus();

  const refetchSession = useCallback(
    () =>
      client.invalidateQueries({
        queryKey: [GROUP_SESSION_DETAIL_QUERY_KEY, sessionId],
      }),
    [client, sessionId]
  );

  const handleSendPaymentLink = useCallback(
    async (patient: CheckInPatient) => {
      if (!patient.email) {
        showSnackbar({
          type: "error",
          message: `${CHECK_IN_MESSAGE.noEmail} ${patient.name}`,
        });
        return;
      }
      if (!patient.amount) {
        showSnackbar({
          type: "error",
          message: `${CHECK_IN_MESSAGE.noAmount} ${patient.name}`,
        });
        return;
      }

      try {
        const res = await sendPaymentLink({
          appointmentId: patient.uuid,
          amount: patient.amount,
          currency: PAYMENT_LINK_CURRENCY,
          resend: patient.paymentLinkSent,
          xTENANTID: getXtenantId(),
        });
        showSnackbar(getSuccessSnackbar(res));
        refetchSession();
      } catch (err) {
        showSnackbar(getErrorSnackbar(err));
      }
    },
    [sendPaymentLink, refetchSession, showSnackbar]
  );

  const handleCollectPayment = useCallback(
    (patient: CheckInPatient) => {
      if (!patient.amount) {
        showSnackbar({
          type: "error",
          message: `${CHECK_IN_MESSAGE.noPaymentAmount} ${patient.name}`,
        });
        return;
      }
      // The drawer charges the patient against this session's appointment.
      paymentDisclosure.open(patient);
    },
    [paymentDisclosure, showSnackbar]
  );

  const onRowAction = useCallback(
    (action: CheckInRowAction, patient: CheckInPatient) => {
      if (action === CHECK_IN_ROW_ACTION.SEND_PAYMENT_LINK) {
        handleSendPaymentLink(patient);
        return;
      }
      if (action === CHECK_IN_ROW_ACTION.COLLECT_PAYMENT) {
        handleCollectPayment(patient);
      }
    },
    [handleSendPaymentLink, handleCollectPayment]
  );

  const startSession = useCallback(async () => {
    if (!sessionId) return;
    const notePath = withSessionParam(
      PROVIDER_PATHS.GROUP_SESSION_NOTE,
      sessionId
    );
    if (sessionStatus === GROUP_APPOINTMENT_STATUS.IN_EXAM) {
      navigate(notePath);
      return;
    }
    try {
      const res = await updateSessionStatus({
        sessionId,
        sessionStatus: UpdateSessionStatusSessionStatusEnum.InExam,
      });
      showSnackbar(getSuccessSnackbar(res));
      refetchSession();
      client.invalidateQueries({ queryKey: [GROUP_SESSION_LIST_QUERY_KEY] });
      navigate(notePath);
    } catch (err) {
      showSnackbar(getErrorSnackbar(err));
    }
  }, [
    sessionId,
    sessionStatus,
    updateSessionStatus,
    showSnackbar,
    refetchSession,
    client,
    navigate,
  ]);

  return { onRowAction, startSession, paymentDisclosure, refetchSession };
};
